import { Suspense } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { logCritical } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { APP_NAMES } from '@savgroup-front-common/constants';
import { DefaultLoader } from '@savgroup-front-common/core/atoms/loader/DefaultLoader';
import { ToastContainer } from '@savgroup-front-common/core/atoms/Toast';
import { TooltipProvider } from '@savgroup-front-common/core/atoms/Tooltip/Tooltip';
import { ConfirmationContextProvider } from '@savgroup-front-common/core/components/Confirmation/ConfirmationContextProvider';
import { HotjarProvider } from '@savgroup-front-common/core/components/HotjarProvider';
import IntlManager from '@savgroup-front-common/core/components/IntlManager/IntlManager';
import { NewUiProvider } from '@savgroup-front-common/core/components/NewUiProvider/NewUiProvider';
import { GlobalStyle } from '@savgroup-front-common/core/helpers/globalStyle';
import { useInitGoogleMap } from '@savgroup-front-common/core/hooks';
import useAppendNoIndexMeta from '@savgroup-front-common/core/hooks/useAppendNoIndexMeta';
import { NetworkStatusProvider } from '@savgroup-front-common/core/molecules/NetworkStatusProvider/NetworkStatusProvider';
import {
  BannersProvider,
  ToastsProvider,
} from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { ExternalPortal } from '@savgroup-front-common/core/protons/ExternalPortal/ExternalPortal';
import defaultTheme from '@savgroup-front-common/core/theme/defaultTheme';
import { getControlTheme } from '@savgroup-front-common/core/theme/Theme';
import { rem } from '@savgroup-front-common/primitive/helpers/units';

import HOTJAR_CONFIG from '../../configuration/hotjar';
import configureStore from '../../store/configureStore';
import ClientInformationProvider from '../atoms/ClientInformationProvider';

import { $InitialLoaderContainer } from './App.styles';
import MyAccountThemeContainer from './MyAccountThemeContainer';
import { MyAccountRoutes } from './Routes/MyAccountRoutes';
import { OnboardingRoutes } from './Routes/OnboardingRoutes';
import useIsOnboardingEnabled from './Routes/useIsOnboardingEnabled';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const shouldEnableReactQueryDevtools =
  import.meta.env.VITE_ENABLE_REACT_QUERY_DEVTOOLS === 'true';

const App = () => {
  try {
    useInitGoogleMap();

    const store = configureStore();
    const isOnboardingEnabled = useIsOnboardingEnabled();

    useAppendNoIndexMeta({
      VITE_ENV: import.meta.env.VITE_ENV as string,
    });

    return (
      <HotjarProvider config={HOTJAR_CONFIG}>
        <ToastsProvider>
          <NetworkStatusProvider>
            <TooltipProvider>
              <BrowserRouter>
                {isOnboardingEnabled && (
                  <NewUiProvider>
                    <ReduxProvider store={store}>
                      <QueryClientProvider client={queryClient}>
                        <BannersProvider>
                          <ThemeProvider theme={getControlTheme({})}>
                            <IntlManager appName={APP_NAMES.AUTHENTICATOR}>
                              {shouldEnableReactQueryDevtools && (
                                <ReactQueryDevtools initialIsOpen={false} />
                              )}

                              <ConfirmationContextProvider>
                                <GlobalStyle myAccount />

                                <ExternalPortal>
                                  <ToastContainer topOffset={rem(84)} />
                                </ExternalPortal>

                                <OnboardingRoutes />
                              </ConfirmationContextProvider>
                            </IntlManager>
                          </ThemeProvider>
                        </BannersProvider>
                      </QueryClientProvider>
                    </ReduxProvider>
                  </NewUiProvider>
                )}

                {!isOnboardingEnabled && (
                  <NewUiProvider>
                    <ReduxProvider store={store}>
                      <QueryClientProvider client={queryClient}>
                        <BannersProvider>
                          <ClientInformationProvider>
                            <Suspense
                              fallback={
                                <$InitialLoaderContainer>
                                  <DefaultLoader
                                    color={defaultTheme.colors.mainTextColor}
                                    size="25px"
                                  />
                                </$InitialLoaderContainer>
                              }
                            >
                              <MyAccountThemeContainer>
                                <IntlManager appName={APP_NAMES.AUTHENTICATOR}>
                                  {shouldEnableReactQueryDevtools && (
                                    <ReactQueryDevtools initialIsOpen={false} />
                                  )}

                                  <ConfirmationContextProvider>
                                    <GlobalStyle myAccount />
                                    <ExternalPortal>
                                      <ToastContainer topOffset={rem(84)} />
                                    </ExternalPortal>

                                    <MyAccountRoutes />
                                  </ConfirmationContextProvider>
                                </IntlManager>
                              </MyAccountThemeContainer>
                            </Suspense>
                          </ClientInformationProvider>
                        </BannersProvider>
                      </QueryClientProvider>
                    </ReduxProvider>
                  </NewUiProvider>
                )}
              </BrowserRouter>
            </TooltipProvider>
          </NetworkStatusProvider>
        </ToastsProvider>
      </HotjarProvider>
    );
  } catch (err) {
    logCritical(err);
    throw err;
  }
};

export default App;
